'use client';

import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { Typography, AlertBlock, ResponsiveModal } from '../../../../../components';
import CustomHeroInput from '../../../../../components/CustomHeroInput';
import partnerAccService from '../../../../../api/partner/partnerAccService';
import { showError, showPartnerSuccess } from '../../../../../utils/toast';
import { LinkIcon } from '../../../../../components/icons';
import { useTranslations } from '../../../../../contexts/translation.context';

interface AddAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddAccountModal: React.FC<AddAccountModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslations();
  const [formData, setFormData] = useState({
    url: '',
  });
  const [accountData, setAccountData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.url.trim()) {
      newErrors.url = t('addAccountModal.urlRequired');
    } else if (!formData.url.includes('onlyfans.com/')) {
      newErrors.url = t('addAccountModal.invalidUrl');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const extractUsername = (url: string): string => {
    const match = url.match(/onlyfans\.com\/([^\/\?]+)/);
    return match ? match[1] : '';
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      const username = extractUsername(formData.url);

      if (!username) {
        showError(t('addAccountModal.extractUsernameError'));
        return;
      }

      if (accountData) {
        const response = await partnerAccService.checkConfirmCode(username);

        if (response.data && response.data.detail === 'Valid code.') {
          showPartnerSuccess(t('addAccountModal.accountAdded'));
          handleClose();
          if (onSuccess) {
            onSuccess();
          }
        } else {
          showError(t('addAccountModal.codeNotFound'));
        }
      } else {
        const response = await partnerAccService.createConfirmCode(username);

        if (response.data) {
          setAccountData({
            username: username,
            displayName: response.data.name || username,
            avatar: response.data.avatar || null,
            verificationCode: response.data.code,
          });
          showPartnerSuccess(t('addAccountModal.accountFound'));
        }
      }
    } catch (error: any) {
      if (accountData) {
        // Показываем реальную ошибку от API или fallback сообщение
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          t('addAccountModal.codeNotFound');
        showError(errorMessage);
      } else {
        // Показываем реальную ошибку от API или fallback сообщение
        const errorMessage =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          t('addAccountModal.checkAccountError');
        showError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ url: '' });
    setAccountData(null);
    setErrors({});
    onClose();
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      placement="center"
      size="2xl"
      classNames={{
        base: 'bg-white md:w-[33.875rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <ModalBody className="bg-white relative rounded-4 size-full p-0">
            <div className="content-stretch flex flex-col items-center overflow-clip relative size-full">
              <div className="content-stretch flex flex-col items-start relative shrink-0 w-full">
                <div className="content-stretch flex flex-col items-center relative shrink-0 w-full">
                  {/* Header */}
                  <div className="box-border content-stretch flex flex-col gap-3 items-start justify-center pb-4 pt-6 px-4 md:px-6 relative shrink-0 w-full">
                    <Typography variant="h6" className="font-medium text-auth-ink">
                      {t('addAccountModal.newAccount')}
                    </Typography>
                  </div>

                  {/* Content */}
                  <div className="box-border content-stretch flex flex-col gap-4 items-center pb-6 pt-3 px-4 md:px-6 relative shrink-0 w-full">
                    {/* URL Input */}
                    <div className="content-stretch flex flex-col gap-2 items-start min-w-[7.25rem] relative shrink-0 w-full">
                      {/* @ts-ignore */}
                      <CustomHeroInput
                        type="text"
                        label={t('addAccountModal.urlLabel')}
                        value={formData.url}
                        onChange={(e) => handleInputChange('url', e.target.value)}
                        placeholder={t('addAccountModal.urlPlaceholder')}
                        error={errors.url}
                        touched={!!errors.url}
                        required
                        className="w-full"
                      />
                      <Typography variant="body3" className="text-zinc-500">
                        {t('addAccountModal.urlHelper')}
                      </Typography>
                    </div>

                    {accountData && (
                      <>
                        <div className="content-stretch flex gap-4 items-center relative shrink-0 w-full">
                          <div className="relative rounded-3 shrink-0 size-10">
                            {accountData.avatar ? (
                              <img
                                alt=""
                                className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none rounded-3 size-full"
                                src={accountData.avatar}
                              />
                            ) : (
                              <div className="absolute inset-0 bg-gray-200 rounded-3 flex items-center justify-center">
                                <span className="text-gray-500 text-xs">?</span>
                              </div>
                            )}
                          </div>
                          <div className="content-stretch flex flex-col items-start justify-center relative shrink-0">
                            <Typography variant="body2" className="font-medium text-auth-ink">
                              {accountData.displayName}
                            </Typography>
                            <div className="content-stretch flex gap-0.5 items-center relative shrink-0">
                              <a
                                target="_blank"
                                href={`https://onlyfans.com/${accountData.username}`}
                                rel="noreferrer"
                              >
                                <Typography variant="body2" className="!text-partner">
                                  {accountData.username}
                                </Typography>
                              </a>
                            </div>
                          </div>
                        </div>

                        <div className="w-full">
                          <AlertBlock
                            data={[
                              {
                                severity: 'info',
                                title: t('addAccountModal.codeVerificationRequired'),
                              },
                            ]}
                            addContent={
                              <div className="mt-2">
                                <Typography
                                  variant="body2"
                                  className="text-auth-muted flex flex-wrap items-center gap-1"
                                >
                                  {t('addAccountModal.addCodeToProfile')}
                                  <span className="bg-husky box-border flex gap-1.5 items-center px-2 py-0.5 rounded-5">
                                    <LinkIcon />
                                    <Typography variant="body2" className="!text-partner">
                                      {accountData.verificationCode}
                                    </Typography>
                                  </span>
                                  {t('addAccountModal.toProfileDescription')}
                                </Typography>
                              </div>
                            }
                            type="default"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="box-border content-stretch flex gap-2 items-center justify-end px-4 md:px-6 py-4 relative shrink-0 w-full">
                  <Button
                    variant="light"
                    onPress={handleClose}
                    disabled={isLoading}
                    className="h-10 px-4 bg-auth-strength-weak/20 text-auth-strength-weak border-none rounded-3"
                  >
                    {t('addAccountModal.close')}
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleSubmit}
                    isLoading={isLoading}
                    disabled={isLoading}
                    className="h-10 px-3.5 bg-partner text-white rounded-3"
                  >
                    {isLoading
                      ? accountData
                        ? t('addAccountModal.checkingCode')
                        : t('addAccountModal.checking')
                      : accountData
                        ? t('addAccountModal.verifyCode')
                        : t('addAccountModal.addAccount')}
                  </Button>
                </div>
              </div>

              <div className="absolute box-border content-stretch flex items-center justify-center overflow-clip pb-0 pt-6 px-4 md:px-6 right-0 size-12 top-0">
                <div className="absolute content-stretch flex items-center justify-center right-2 size-8 top-2">
                  <Button
                    isIconOnly
                    variant="light"
                    onPress={handleClose}
                    className="p-1.5 rounded-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                </div>
              </div>
            </div>
          </ModalBody>
        )}
      </ModalContent>
    </ResponsiveModal>
  );
};

export default AddAccountModal;
